import { useMotionValueEvent, useScroll } from 'framer-motion';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import RoundArrowSVG from 'public/icons/round-arrow.svg';
import { useRef, useState } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { TeamPerson } from '@/entities';
import { Day } from '@/entities/day';
import { Direction } from '@/entities/direction';
import { Format } from '@/entities/format';
import { Place } from '@/entities/place';
import { getCommonPageProps } from '@/shared/api';
import { getDays } from '@/shared/api/days';
import { getDirections } from '@/shared/api/directions';
import { getEmployees } from '@/shared/api/employees';
import { getPageDetail } from '@/shared/api/page-detail';
import { getFormats } from '@/shared/api/participation-formats';
import { getPlaces } from '@/shared/api/places';
import { ApiProgramRequest, AppProgramResponse, getProgram } from '@/shared/api/program';
import { getQuestions } from '@/shared/api/questions';
import { getSiteSettings } from '@/shared/api/site-settings';
import { useHeaderThemeValue } from '@/shared/atoms/header-theme';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import SubscribePopup from '@/shared/components/popups/SubscribePopup';
import FaqSection, { FaqSectionProps } from '@/shared/components/sections/FaqSection';
import ProgramDetailSection from '@/shared/components/sections/ProgramDetailSection';
import { ProgramDetailSectionProps } from '@/shared/components/sections/ProgramDetailSection/ProgramDetailSection';
import QuestionsSection, { QuestionsSectionProps } from '@/shared/components/sections/QuestionsSection';
import Breadcrumbs from '@/shared/components/shared/Breadcrumbs';
import DownloadPdfBtn from '@/shared/components/shared/DownloadPdfBtn/DownloadPdfBtn';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import Link from '@/shared/components/shared/Link';
import ScrollTopButton from '@/shared/components/shared/ScrollTopButton';
import Tooltip from '@/shared/components/shared/Tooltip';
import Typografed from '@/shared/components/shared/Typografed';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateProgramEventJsonLd } from '@/shared/lib/utils/event-json-ld';
import { filterFaqQuestionsVisibleInFaqSection } from '@/shared/lib/utils/faq-json-ld';
import { splitSubtitlePreservingRest } from '@/shared/lib/utils/split-subtitle-preserving-rest';

const ProgramPage = ({
    h1,
    subtitle,
    breadcrumbs,
    footerData,
    programDetailSectionProps,
    places,
    days,
    formats,
    directions,
    initialParams = {},
    initialProgramResponse,
    questionsSectionProps,
    contactPersonProps,
    faqSectionProps,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const ref = useRef(null);
    const [isButtonVisible, setIsButtonVisible] = useState(false);
    const theme = useHeaderThemeValue();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });

    useMotionValueEvent(scrollYProgress, 'change', (progress) => {
        setIsButtonVisible(progress > 0);
    });

    const [part1, part2] = splitSubtitlePreservingRest(subtitle);

    return (
        <DefaultLayout currentPageId={PAGES.PROGRAM} ref={ref}>
            <div className="news-page-top js-header-theme-trigger wrapper" data-theme="dark" id="top">
                <Breadcrumbs className="news-page-breadcrumbs" breadcrumbs={breadcrumbs} />
                <div className="program-detail-title-block">
                    <div className="program-detail-title-block-text">
                        {subtitle && (
                            <div className="program-main-article text-m">
                                <span dangerouslySetInnerHTML={{ __html: part1 }} />
                                <span dangerouslySetInnerHTML={{ __html: part2 }} />
                            </div>
                        )}
                        <h1 className="program-detail-title h2">
                            <Typografed>{h1}</Typografed>
                        </h1>
                    </div>
                    <div className="program-detail-title-btns">
                        {/* временно скрываю, заказчик не знает нужна ли */}
                        {/* 
                            <Tooltip message="Тур по TNF 2024">
                                <Link href="/tour-tnf/#tour" className="program-detail-360-btn text-xs">
                                    360°
                                    <RoundArrowSVG className="round-arrow-svg" />
                                </Link>
                            </Tooltip>
                         */}

                        <DownloadPdfBtn />
                    </div>
                </div>
            </div>

            <ProgramDetailSection
                reflectFilterStateInUrl
                programDetailSectionProps={programDetailSectionProps}
                isButtonVisible={isButtonVisible}
                days={days}
                directions={directions}
                places={places}
                formats={formats}
                initialParams={initialParams}
                initialResponse={initialProgramResponse}
                mainTheme={h1}
                pdfTitle="Главная тема Форума TNF 2026 <br/> Нефтегазовая отрасль будущего: энергия лидерства"
            />

            {filterFaqQuestionsVisibleInFaqSection(faqSectionProps.questions).length > 0 && (
                <FaqSection faqSectionProps={faqSectionProps} />
            )}
            <QuestionsSection
                questionsSectionProps={questionsSectionProps}
                contactPersonProps={contactPersonProps}
                className="js-header-theme-trigger"
                data-theme="dark"
                id="registration"
            />
            <SubscribePopup />
            <ScrollTopButton theme={theme} scrollYProgress={scrollYProgress} universal />
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-program"
                data={generateProgramEventJsonLd({
                    h1,
                    subtitle,
                    events: initialProgramResponse.data?.items || [],
                    days,
                    places,
                    pagePath: '/program/',
                    breadcrumbs,
                    faqQuestions: faqSectionProps.questions,
                    contacts: footerData,
                })}
            />
        </DefaultLayout>
    );
};

export default ProgramPage;

type ProgramPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        subtitle: string;
        programDetailSectionProps: ProgramDetailSectionProps;
        days: Day[];
        formats: Format[];
        places: Place[];
        directions: Direction[];
        initialParams: ApiProgramRequest;
        initialProgramResponse: AppProgramResponse;
        questionsSectionProps: QuestionsSectionProps;
        contactPersonProps: TeamPerson | null;
        faqSectionProps: FaqSectionProps;
    }
>;

export const getServerSideProps: GetServerSideProps<ProgramPageProps> = async ({ query }) => {
    const [pageResponse, newProgramPageResponse] = await Promise.all([
        getPageDetail({ params: { slug: 'program-2026' } }),
        getPageDetail({ params: { slug: 'new-program' } }),
    ]);
    const initialParams: ApiProgramRequest = {};
    const newProgramPageId = newProgramPageResponse.data?.id ?? PAGES.NEW_PROGRAM;
    const [
        commonPageProps,
        [days, programDetailResponse],
        placesResponse,
        directionsResponse,
        formatsResponse,
        questionsResponse,
        employeesResponse,
        siteSettings,
    ] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.NEW_PROGRAM),
        getDays({ params: { page: pageResponse.data?.id ?? PAGES.NEW_PROGRAM } }).then((daysResponse) => {
            const days = daysResponse.data || [];
            const dayIds = days.map((day) => day.id);

            if (typeof query.day === 'string') {
                initialParams.day = parseInt(query.day);
            }

            if (typeof query.directions === 'string') {
                initialParams.directions = query.directions.split(',').map((id) => parseInt(id.trim()));
            }

            if (typeof query.q === 'string') {
                initialParams.q = query.q;
            }

            return Promise.all([
                days,
                getProgram({
                    params: {
                        ...initialParams,
                        days: typeof initialParams.day === 'number' ? [initialParams.day] : days.map((day) => day.id),
                    },
                }),
                getProgram({
                    params: {
                        days: dayIds,
                        is_free: 1,
                    },
                }),
            ]);
        }),
        getPlaces(),
        getDirections(),
        getFormats({ params: { page: pageResponse.data?.id ?? PAGES.NEW_PROGRAM } }),
        getQuestions({ params: { page: newProgramPageId } }),
        getEmployees({ params: { page: newProgramPageId } }),
        getSiteSettings({
            params: {
                keys: [
                    'new_program_first_screen_title',
                    'new_program_first_screen_subtitle',
                    'new_program_faq_title',
                    'new_program_form_title',
                    'new_program_form_description',
                ],
            },
        }),
    ]);

    const formats = formatsResponse.data || [];
    const directions = directionsResponse.data || [];
    const places = placesResponse.data || [];
    return {
        props: {
            ...commonPageProps,
            bodyClass: 'program-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Программа', href: '/program/' },
            ],
            h1: siteSettings.data?.new_program_first_screen_title || '',
            subtitle: siteSettings.data?.new_program_first_screen_subtitle || '',
            programDetailSectionProps: {
                cards: programDetailResponse.data?.items || [],
            },
            directions,
            formats,
            days,
            places,
            initialParams,
            initialProgramResponse: programDetailResponse,
            faqSectionProps: {
                title: siteSettings.data?.new_program_faq_title ?? '',
                questions: questionsResponse.data ?? [],
            },
            questionsSectionProps: {
                title: siteSettings.data?.new_program_form_title ?? '',
                description: siteSettings.data?.new_program_form_description ?? '',
            },
            contactPersonProps: employeesResponse.data?.[0] || null,
        },
    };
};
